<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { message as antMessage } from 'ant-design-vue'
import { useUserStore } from '@/stores'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons-vue'
import { MessageBubble, MessageInput } from '@/modules/message'
import {
  sendMessageToContact,
  markMessageAsRead,
  markMessagesAsRead,
  translateMessage as translateMessageApi,
  getUnreadCount as getUnreadCountApi,
  getUnreadCountByContact,
  getGroupContacts,
  getConversationMessages,
  type MessageResponse,
  type UserSummary,
  MessageType
} from '@/modules/message/api'

const userStore = useUserStore()
const router = useRouter()
const { t } = useI18n()
const currentUserId = computed(() => userStore.userInfo?.id || 0)

// 联系人 & 消息状态
const loadingContacts = ref(false)
const contactSearch = ref('')
const contacts = ref<UserSummary[]>([])
const filteredContacts = ref<UserSummary[]>([])
const selectedContact = ref<UserSummary | null>(null)

const loadingMessages = ref(false)
const currentMessages = ref<MessageResponse[]>([])
const translatingMessages = ref(new Set<number>())
const messageContainer = ref<HTMLElement>()

// 未读计数
const unreadCounts = reactive<Record<number, number>>({})
const totalUnreadCount = ref(0)

const hasUnreadMessages = computed(() =>
  currentMessages.value.some(
    msg => !msg.is_read && msg.sender_id !== currentUserId.value
  )
)

// 轮询
const POLLING_INTERVAL_ACTIVE_MS = 15000
const POLLING_INTERVAL_HIDDEN_MS = 60000
let pollingTimer: ReturnType<typeof setInterval> | null = null

const startPolling = (intervalMs: number) => {
  if (pollingTimer) clearInterval(pollingTimer)
  pollingTimer = setInterval(async () => {
    await checkNewMessages()
    await updateUnreadCounts()
  }, intervalMs)
}

const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    startPolling(POLLING_INTERVAL_ACTIVE_MS)
  } else {
    startPolling(POLLING_INTERVAL_HIDDEN_MS)
  }
}

// 加载联系人
const loadContacts = async () => {
  try {
    loadingContacts.value = true
    const params: any = {}
    if (contactSearch.value.trim()) {
      params.search = contactSearch.value.trim()
    }
    const response = await getGroupContacts(params)
    contacts.value = response.contacts
    filteredContacts.value = contacts.value
    await updateUnreadCounts()
  } catch (error) {
    console.error('加载联系人失败:', error)
  } finally {
    loadingContacts.value = false
  }
}

const filterContacts = async () => {
  await loadContacts()
}

const selectContact = async (contact: UserSummary) => {
  selectedContact.value = contact
  await loadMessages()
}

const refreshContacts = () => {
  loadContacts()
}

// 加载消息
const loadMessages = async () => {
  if (!selectedContact.value) return
  try {
    loadingMessages.value = true
    const response = await getConversationMessages(selectedContact.value.id, {
      page: 1,
      page_size: 100
    })
    currentMessages.value = response.items

    // 当前会话未读标为已读，并立即清零该联系人角标（乐观更新，避免角标不消失）
    const unreadInConversation = response.items.filter(
      msg => !msg.is_read && msg.receiver_id === currentUserId.value
    )
    if (unreadInConversation.length > 0) {
      try {
        await markMessagesAsRead(unreadInConversation.map(msg => msg.id))
        unreadInConversation.forEach(msg => { msg.is_read = true })
        const contactId = selectedContact.value?.id
        if (contactId !== undefined) {
          const prev = unreadCounts[contactId] ?? 0
          unreadCounts[contactId] = 0
          totalUnreadCount.value = Math.max(0, totalUnreadCount.value - prev)
        }
        await updateUnreadCounts()
      } catch (e) {
        console.error('标记会话已读失败:', e)
      }
    }

    await nextTick()
    scrollToBottom()
  } catch (error) {
    console.error('加载消息失败:', error)
    antMessage.error(t('message.loadMessagesFailed'))
  } finally {
    loadingMessages.value = false
  }
}

// 发送消息
const handleSendMessage = async (content: string) => {
  if (!selectedContact.value) return
  try {
    const response = await sendMessageToContact({
      receiver_id: selectedContact.value.id,
      content,
      message_type: MessageType.USER
    })
    currentMessages.value.push(response)
    await nextTick()
    scrollToBottom()
    antMessage.success(t('message.messageSent'))
  } catch (error) {
    console.error('发送消息失败:', error)
    antMessage.error(t('message.sendMessageFailed'))
  }
}

// 翻译消息
const translateMessage = async (message: MessageResponse) => {
  try {
    translatingMessages.value.add(message.id)
    const translatedMessage = await translateMessageApi(message.id, false)
    const idx = currentMessages.value.findIndex(msg => msg.id === message.id)
    if (idx !== -1) currentMessages.value[idx] = translatedMessage
    antMessage.success(t('message.messageTranslatedSuccess'))
  } catch (error) {
    console.error('翻译消息失败:', error)
    antMessage.error(t('message.translateMessageFailed'))
  } finally {
    translatingMessages.value.delete(message.id)
  }
}

const handleTranslateMessage = (messageId: number) => {
  const message = currentMessages.value.find(msg => msg.id === messageId)
  if (message) translateMessage(message)
}

// 未读计数
const updateUnreadCounts = async () => {
  try {
    const totalResponse = await getUnreadCountApi()
    totalUnreadCount.value = totalResponse.unread_count
    const contactResponse = await getUnreadCountByContact()
    Object.assign(unreadCounts, contactResponse.unread_counts)
  } catch (error) {
    console.error('更新未读计数失败:', error)
  }
}

const getUnreadCount = (contactId: number): number => unreadCounts[contactId] || 0

// 标记已读
const markAsRead = async (messageId: number) => {
  try {
    await markMessageAsRead(messageId)
    const idx = currentMessages.value.findIndex(msg => msg.id === messageId)
    if (idx !== -1) currentMessages.value[idx].is_read = true
  } catch (error) {
    console.error('标记已读失败:', error)
  }
}

const markAllAsRead = async () => {
  const unreadMessages = currentMessages.value.filter(
    msg => !msg.is_read && msg.receiver_id === currentUserId.value
  )
  if (!unreadMessages.length) return
  try {
    await markMessagesAsRead(unreadMessages.map(msg => msg.id))
    unreadMessages.forEach(msg => { msg.is_read = true })
    await updateUnreadCounts()
    antMessage.success(t('message.allMarkedAsRead'))
  } catch (error) {
    console.error('批量标记已读失败:', error)
    antMessage.error(t('message.markAllAsReadFailed'))
  }
}

const scrollToBottom = () => {
  if (messageContainer.value) {
    const container = messageContainer.value.querySelector('.message-list') as HTMLElement | null
    if (container) container.scrollTop = container.scrollHeight
  }
}

const refreshMessages = () => {
  loadMessages()
}

// 轮询新消息
const checkNewMessages = async () => {
  if (!selectedContact.value) return
  try {
    const response = await getConversationMessages(selectedContact.value.id, {
      page: 1,
      page_size: 100
    })
    const newMessages = response.items
    const currentIds = new Set(currentMessages.value.map(msg => msg.id))
    const hasNew = newMessages.some(msg => !currentIds.has(msg.id))
    if (!hasNew) return

    currentMessages.value = newMessages
    await nextTick()
    scrollToBottom()

    const unread = newMessages.filter(
      msg =>
        !msg.is_read &&
        msg.receiver_id === currentUserId.value &&
        !currentIds.has(msg.id)
    )
    if (unread.length) {
      antMessage.info(t('message.receivedNewMessages', { count: unread.length }))
      await markMessagesAsRead(unread.map(msg => msg.id))
      unread.forEach(msg => { msg.is_read = true })
    }
  } catch (error) {
    console.error('检查新消息失败:', error)
  }
}

// 生命周期
onMounted(async () => {
  if (!userStore.userInfo || !userStore.token) {
    antMessage.warning(t('message.pleaseLoginFirst'))
    router.push('/login')
    return
  }
  await loadContacts()
  handleVisibilityChange()
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  stopPolling()
})

watch(selectedContact, () => {
  if (selectedContact.value) {
    loadMessages()
  }
})
</script>

<template>
  <div class="research-messages">
    <div class="collaboration-layout">
      <!-- 左侧联系人 -->
      <div class="contacts-panel">
        <a-card :title="$t('message.contacts')" size="small" :bordered="false" class="contacts-card">
          <template #extra>
            <a-button type="text" size="small" @click="refreshContacts">
              <template #icon><ReloadOutlined /></template>
            </a-button>
          </template>

          <div class="contact-search">
            <a-input
              v-model:value="contactSearch"
              :placeholder="$t('message.searchContacts')"
              size="small"
              @input="filterContacts"
            >
              <template #prefix><SearchOutlined /></template>
            </a-input>
          </div>

          <div class="contact-list">
            <div
              v-for="contact in filteredContacts"
              :key="contact.id"
              :class="['contact-item', { active: selectedContact?.id === contact.id }]"
              @click="selectContact(contact)"
            >
              <a-avatar :size="32" class="contact-avatar">
                {{ contact.name.charAt(0) }}
              </a-avatar>
              <div class="contact-info">
                <div class="contact-name">{{ contact.name }}</div>
                <div class="contact-detail">
                  <span v-if="contact.name_remark" class="contact-remark">
                    {{ contact.name_remark }}
                  </span>
                  <span class="contact-language">
                    {{ contact.language || $t('message.languageNotSet') }}
                  </span>
                </div>
              </div>
              <a-badge
                v-if="getUnreadCount(contact.id) > 0"
                :count="getUnreadCount(contact.id)"
                class="contact-badge"
              />
            </div>
          </div>
        </a-card>
      </div>

      <!-- 中间消息区 -->
      <div class="messages-panel">
        <a-card :bordered="false" class="messages-card">
          <template #title>
            <div class="message-header">
              <div v-if="selectedContact" class="chat-info">
                <a-avatar :size="32">{{ selectedContact.name.charAt(0) }}</a-avatar>
                <div class="chat-details">
                  <div class="chat-name">{{ selectedContact.name }}</div>
                  <div class="chat-status">
                    <span v-if="selectedContact.name_remark" class="chat-remark">
                      {{ selectedContact.name_remark }}
                    </span>
                    <span class="chat-language">
                      {{ selectedContact.language || $t('message.languageNotSet') }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="no-chat-selected">
                {{ $t('message.selectContactToStart') }}
              </div>
            </div>
          </template>

          <template #extra>
            <a-space>
              <a-button
                v-if="selectedContact"
                type="text"
                size="small"
                @click="refreshMessages"
                :loading="loadingMessages"
              >
                <template #icon><ReloadOutlined /></template>
              </a-button>
              <a-button
                v-if="selectedContact && hasUnreadMessages"
                type="text"
                size="small"
                @click="markAllAsRead"
              >
                {{ $t('message.markAllAsRead') }}
              </a-button>
            </a-space>
          </template>

          <div class="message-container" ref="messageContainer">
            <div v-if="!selectedContact" class="no-contact-selected">
              <a-empty :description="$t('message.pleaseSelectContact')" />
            </div>

            <div v-else-if="loadingMessages" class="loading-messages">
              <a-spin size="large" />
            </div>

            <div v-else class="message-list">
              <MessageBubble
                v-for="message in currentMessages"
                :key="message.id"
                :message="message"
                :current-user-id="currentUserId"
                :translating="translatingMessages.has(message.id)"
                @translate="handleTranslateMessage"
                @mark-read="markAsRead"
              />
              <div v-if="currentMessages.length === 0" class="no-messages">
                <a-empty :description="$t('message.noMessageRecords')" />
              </div>
            </div>
          </div>

          <div v-if="selectedContact" class="message-input">
            <MessageInput
              :receiver-id="selectedContact.id"
              @send="handleSendMessage"
            />
          </div>
        </a-card>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.research-messages {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.collaboration-layout {
  display: flex;
  height: 100%;
  gap: 16px;
  padding: 16px;
  background-color: #f5f5f5;
}

.contacts-panel,
.messages-panel {
  background-color: white;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.contacts-panel {
  width: 280px;
  flex-shrink: 0;
}

.contacts-card {
  height: 100%;
  border: none;
  box-shadow: none;

  :deep(.ant-card-head) {
    border-bottom: 1px solid #f0f0f0;
    background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
    min-height: 60px;
    padding: 0 20px;

    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
      color: #262626;
    }
  }

  :deep(.ant-card-body) {
    padding: 16px;
    height: calc(100% - 60px);
    display: flex;
    flex-direction: column;
  }
}

.contact-search {
  margin-bottom: 16px;
}

.contact-list {
  flex: 1;
  overflow-y: auto;
  max-height: 600px;
}

.contact-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;

  &:hover {
    background-color: #f0f0f0;
  }

  &.active {
    background-color: #e6f7ff;
    border: 1px solid #91d5ff;
  }
}

.contact-avatar {
  margin-right: 12px;
  background-color: #1890ff;
}

.contact-info {
  flex: 1;
  min-width: 0;
}

.contact-name {
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-detail {
  font-size: 12px;
  color: #666;
  display: flex;
  gap: 8px;
}

.contact-remark {
  color: #999;
}

.contact-language {
  color: #1890ff;
}

.contact-badge {
  position: absolute;
  top: 8px;
  right: 8px;
}

.messages-panel {
  flex: 1;
  min-width: 0;
}

.messages-card {
  height: 100%;
  border: none;
  box-shadow: none;

  :deep(.ant-card-head) {
    border-bottom: 1px solid #f0f0f0;
    background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
    min-height: 60px;
    padding: 0 20px;
    display: flex;
    align-items: center;

    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
      color: #262626;
      line-height: 1.2;
    }

    .ant-card-extra {
      line-height: 1.2;
    }
  }

  :deep(.ant-card-body) {
    padding: 0;
    height: calc(100% - 60px);
    display: flex;
    flex-direction: column;
  }
}

.message-header {
  display: flex;
  align-items: center;
}

.chat-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-details {
  display: flex;
  flex-direction: column;
}

.chat-name {
  font-weight: 500;
  font-size: 16px;
}

.chat-status {
  font-size: 12px;
  color: #666;
  display: flex;
  gap: 8px;
}

.chat-remark {
  color: #999;
}

.chat-language {
  color: #1890ff;
}

.no-chat-selected {
  color: #999;
  font-size: 16px;
}

.message-container {
  flex: 1;
  padding: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.no-contact-selected,
.loading-messages {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
}

.no-messages {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-input {
  padding: 16px;
  border-top: 1px solid #f0f0f0;
}

.contact-list::-webkit-scrollbar,
.message-list::-webkit-scrollbar {
  width: 4px;
}

.contact-list::-webkit-scrollbar-track,
.message-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.contact-list::-webkit-scrollbar-thumb,
.message-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.contact-list::-webkit-scrollbar-thumb:hover,
.message-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

@media (max-width: 1200px) {
  .collaboration-layout {
    gap: 12px;
    padding: 12px;
  }

  .contacts-panel {
    width: 240px;
    min-width: 240px;
  }
}

@media (max-width: 768px) {
  .research-messages {
    height: calc(100vh - 64px);
  }

  .collaboration-layout {
    flex-direction: column;
    height: 100%;
    gap: 12px;
    padding: 12px;
  }

  .contacts-panel {
    width: 100%;
    min-width: auto;
    height: 250px;
  }

  .messages-panel {
    min-width: auto;
    flex: 1;
    min-height: 300px;
  }
}
</style>
